import React, { useEffect, useMemo } from 'react';
import { DateObj, useDayzed } from 'dayzed';
import { useLocale } from '@honeybook/i18n-react';
import { getFirstDayOfWeek } from '@honeybook/i18n';
import styled, { css } from 'styled-components';
import Skeleton from 'react-loading-skeleton';
import HBClickable from '../HBClickable/HBClickable';
import HBText from '../HBText/HBText';
import { ArrowLeft16, ArrowRight16 } from '@honeybook/icons';
import DayButton from './DayButton';
import { applyOpacity } from '../../utils/color.utils';
import type {
    IDayPickerBaseProps,
    IDayzedProps,
    IDayzedMonth
} from './HBDayPicker.types';
import { vars } from '../../theme';
import type { TCustomFont } from '../../styles/fonts';
import { useFocusGroup } from '../../hooks/useFocusGroup';

// TODO: what about i18n? since we don't use react-i18n in HBUI, we'll probably need to propify these.
const WEEKDAY_NAMES_SHORT = [
    'Sun',
    'Mon',
    'Tue',
    'Wed',
    'Thu',
    'Fri',
    'Sat'
] as const;

export const MONTH_NAMES = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
] as const;

export interface HBDayPickerProps
    extends IDayPickerBaseProps,
        Pick<IDayzedProps, 'firstDayOfWeek' | 'onDateSelected'> {
    /** The selected date or array of selected dates passed to dayzed. Controls the highlighted day(s) in the calendar. */
    selectedDate: IDayzedProps['selected'];
    /** Called when the user clicks a selectable day. Receives the dayzed `DateObj` (with `date`, `selectable`, `selected`, etc.) and the originating event. */
    onDateSelected: IDayzedProps['onDateSelected'];
    /** Day the week starts on: 0 = Sunday, 1 = Monday, … 6 = Saturday. Defaults to the user's locale value when omitted. */
    firstDayOfWeek?: IDayzedProps['firstDayOfWeek'];
    /** Called when the displayed month changes via navigation arrows, receives `{ month, year }`. */
    onMonthChange?: (dayzedMonth: IDayzedMonth) => void;
    /** Earliest selectable date; dates before this are disabled in the calendar. */
    minDate?: Date;
    /** Custom font family applied to all text within the picker. */
    fontFamily?: TCustomFont;
    /** Shows skeleton placeholders for the entire calendar while true. */
    isLoading?: boolean;
    /** Shows skeleton placeholders only on day cells while the current month's data loads. */
    isFetchingMonth?: boolean;
    /** Initial date used to determine which month the calendar displays on mount. */
    startDate?: Date;
}

const HBDayPicker = ({
    selectedDate,
    onDateSelected,
    isDisabled,
    fontFamily,
    firstDayOfWeek: firstDayOfWeekProp,
    baseTextColor = vars.colors.text.primary,
    availabilityStatusTheme,
    backgroundColor = vars.colors.text.primary,
    selectedTextColor = vars.colors.text.invertedPrimary,
    isDateSelectable = Boolean,
    onMonthChange,
    minDate,
    isLoading,
    isFetchingMonth,
    startDate
}: HBDayPickerProps) => {
    const { locale } = useLocale();
    const firstDayOfWeek = firstDayOfWeekProp ?? getFirstDayOfWeek(locale);

    const { calendars, getBackProps, getForwardProps, getDateProps } =
        useDayzed({
            date: startDate,
            selected: selectedDate,
            onDateSelected,
            firstDayOfWeek,
            minDate
        });

    const relevantCalendar = (calendars && calendars[0]) || null;

    const rotatedWeekdayNamesShort = useMemo(
        () =>
            WEEKDAY_NAMES_SHORT.slice(firstDayOfWeek).concat(
                WEEKDAY_NAMES_SHORT.slice(0, firstDayOfWeek)
            ),
        [firstDayOfWeek]
    );

    useEffect(() => {
        if (!!relevantCalendar) {
            onMonthChange?.({
                month: relevantCalendar.month,
                year: relevantCalendar.year
            });
        }
    }, [relevantCalendar, onMonthChange]);

    useFocusGroup(`${ArrowsWrapper} ${ArrowButton}`);
    useFocusGroup(`${Container} ${StyledDayButton}`);

    if (!calendars.length) {
        return null;
    }
    const { month, year, weeks } = calendars[0];

    const _isDisabled = isDisabled || isLoading;

    const getDayProps = (dateObj: '' | DateObj) => {
        if (dateObj) {
            const { disabled, ...props } = getDateProps({ dateObj });
            return props;
        }
    };

    const testId = 'hb-day-picker';

    const renderArrow = (isBack: boolean) => {
        const [getProps, Arrow16] = isBack
            ? [getBackProps, ArrowLeft16]
            : [getForwardProps, ArrowRight16];

        const { disabled, ...props } = getProps({ calendars });

        const __isDisabled = _isDisabled || disabled;

        const directionText = isBack ? 'back' : 'forward';

        return (
            <ArrowButton
                onClick={() => {}}
                isDisabled={__isDisabled}
                color={baseTextColor}
                testId={`${testId}-${directionText}--button`}
                {...props}
            >
                {isLoading ? <Skeleton width={16} height={16} /> : <Arrow16 />}
            </ArrowButton>
        );
    };

    return (
        <Container
            fontFamily={fontFamily}
            textColor={baseTextColor}
            isDisabled={_isDisabled}
            data-testid={`${testId}--wrapper`}
        >
            <MonthYear type="emphasis16" testId="HBDayPicker-header">
                {isLoading ? (
                    <Skeleton width={140} />
                ) : (
                    `${MONTH_NAMES[month]} ${year}`
                )}
            </MonthYear>
            <ArrowsWrapper>
                {renderArrow(true)}
                {renderArrow(false)}
            </ArrowsWrapper>

            {rotatedWeekdayNamesShort.map(weekday => (
                <WeekDayName
                    type="emphasis12"
                    key={weekday}
                    testId={`HBDayPicker-weekday-${weekday}`}
                >
                    {isLoading ? <Skeleton width={24} /> : weekday}
                </WeekDayName>
            ))}

            {weeks.flat().map((dateObj, dayIndex) => (
                <StyledDayButton
                    key={`${month}${dayIndex}`}
                    dayzedDateObject={dateObj}
                    dayzedDateProps={getDayProps(dateObj)}
                    isDisabled={_isDisabled}
                    isLoading={isLoading || isFetchingMonth}
                    backgroundColor={backgroundColor}
                    baseTextColor={baseTextColor}
                    availabilityStatusTheme={availabilityStatusTheme}
                    selectedTextColor={selectedTextColor}
                    isDateSelectable={isDateSelectable}
                />
            ))}
        </Container>
    );
};

export default HBDayPicker;

// --- Styles ---

const Container = styled.div<{
    fontFamily: TCustomFont;
    textColor: string;
    isDisabled: boolean;
}>`
    color: ${({ textColor }) => textColor || vars.colors.text.primary};
    ${({ fontFamily }) =>
        fontFamily &&
        css`
            &,
            * {
                font-family: ${fontFamily};
            }
        `};

    user-select: ${({ isDisabled }) => (isDisabled ? 'none' : 'initial')};
    max-width: 280px;
    height: fit-content;
    display: grid;
    grid-template-rows: repeat(2, auto) repeat(6, 1fr);
    grid-template-columns: repeat(7, 1fr);
`;

const MonthYear = styled(HBText)`
    grid-column: span 5;
`;

const ArrowsWrapper = styled.div`
    grid-column: span 2;
    justify-self: end;
    display: flex;
    position: relative;
    right: -10px;
    top: -6px;
`;

const ArrowButton = styled(HBClickable)<{ color: string }>`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 32px;
    width: 36px;
    i {
        line-height: 0px;
    }
    ${({ isDisabled, color }) =>
        isDisabled
            ? css`
                  svg * {
                      fill: ${applyOpacity(color, 20)};
                  }
              `
            : css`
                  svg * {
                      fill: ${applyOpacity(color, 60)};
                  }
                  &:hover,
                  &:focus-visible {
                      svg * {
                          fill: ${color};
                      }
                  }
              `}
`;

const WeekDayName = styled(HBText)`
    justify-self: center;
    margin-bottom: 12px;
`;

const StyledDayButton = styled(DayButton)`
    margin: 2px;
`;
